/**
 * Onboarding — create an Organization, its first admin, and that admin's
 * credential.
 *
 * ===========================================================================
 * THIS BROWSER HOLDS THE ONLY COPY OF THE PASSWORD IN EXISTENCE
 * ===========================================================================
 *
 * Not the operator's only copy — the ONLY copy. `0026` option B: this console
 * generates the password and derives from it, and `onboardOrganizationOutput`
 * carries no credential field of any kind. Core stores a verifier it cannot
 * invert. If this screen loses the value, the account is unreachable and the
 * only remedy is a credential reset.
 *
 * So the credential panel is the most carefully built thing on this page:
 *
 *   - IT IS NOT RENDERED UNTIL CORE ANSWERS 201. Showing it beside a request
 *     that then failed hands an operator a credential for an account that does
 *     not exist, and afterwards they cannot tell which they are holding.
 *   - IT IS SHOWN ONCE and says so before it is dismissed, not after.
 *   - DISMISSING IT REQUIRES A DELIBERATE CONFIRMATION, because the alternative
 *     is a mis-click that destroys the only copy of a credential.
 *   - IT IS NEVER PERSISTED. React state for the life of this screen, dropped on
 *     unmount. No storage, no URL, no log.
 *
 * ===========================================================================
 * THE WORKSPACE NAME IS NOT ASKED FOR, AND THE SCREEN SAYS WHY
 * ===========================================================================
 *
 * `first_workspace_name` is required by the contract, validated by Core, and
 * discarded — `business` has two columns and naming belongs to the
 * organization-structure slice. Team Lead ruling: send a fixed placeholder and
 * do not prompt. An operator who types "Main Campus" and watches it vanish has
 * been lied to by the form, and nothing is preserved for later.
 *
 * ===========================================================================
 * THE NAME IS OPTIONAL HERE, AND THE REGISTRATIONS ARE NOT ASKED FOR AT ALL
 * ===========================================================================
 *
 * `display_name` is OPTIONAL on this write path — Team Lead ruling, 2026-09-07,
 * on sequencing rather than design: "a server requiring a field the deployed
 * console does not yet send is an onboarding outage." So this form accepts a
 * name and never requires one, and an operator who does not have it yet can
 * onboard now and record it on the Organization's own page afterwards.
 *
 * THE CR AND THE VAT REGISTRATION ARE NOT ON THIS FORM, AND THAT IS THE
 * CONTRACT RATHER THAN A CHOICE. `onboardOrganizationInput` accepts
 * `admin_identifier`, `display_name`, `template_id`, `first_workspace_name` and
 * `derived_value`, with `additionalProperties: false` — there is no field for
 * either registration, and every Organization starts `not_recorded` by design.
 * They are recorded on the detail page, which the success panel links to.
 *
 * ===========================================================================
 * A 201 WITH WARNINGS IS A SUCCESS
 * ===========================================================================
 *
 * `workspace_id: null` plus `first_workspace_not_created` means the Organization
 * exists, the admin exists, the credential is live, and the tenant-side write
 * failed. Rendering that as an error would be wrong twice: the operator would
 * believe nothing was created, and would discard a credential that is the only
 * way into a real customer.
 */

import { useCallback, useState, type FormEvent } from 'react';
import { Button, Input } from '@dudo/ui';
import { AdminField as Field } from '@/components/AdminField';
import { LoadingBlock } from '@/components/StateBlock';
import { cn } from '@dudo/ui';
import { Link } from '@tanstack/react-router';
import { identifierRefusal } from '@dudo/client-kdf';
import type { DerivationProgress } from '@dudo/client-kdf/client';
import { createOnboardingCredential } from '@/api/onboarding-credential';
import { useOnboardOrganization, useTemplatePicker } from '@/lib/queries';
import { formatSeconds, refusalText, useLocale, useT } from '@/lib/i18n';
import {
  MAX_DISPLAY_NAME_LENGTH,
  displayNameRefusal,
  isKnownOnboardingWarning,
  type OnboardOrganizationOutput,
  type Template,
} from '@/api/platform';
import { toApiError, type ApiError } from '@/api/errors';

/**
 * ===========================================================================
 * THE NAME FIELD WAS BRIEFLY UNSENDABLE, AND THE REASON OUTLIVES THE GATE.
 * ===========================================================================
 *
 * `organization-onboarding-v1.schema.json` published `display_name` while
 * `platform.organizations.create` declared only four fields —
 * `admin_identifier`, `template_id`, `first_workspace_name`, `derived_value`.
 * **The platform class refuses any undeclared field BEFORE AUTHENTICATION**, so
 * a client that trusted the contract would have failed the first onboarding
 * carrying the field and every one after it, on the route that creates
 * customers.
 *
 * *** SO READING THE CONTRACT WAS NOT EVIDENCE THAT CORE ACCEPTED THE FIELD. ***
 * That is the sentence worth keeping. Core landed it on 2026-09-07 — declared,
 * parsed optionally with the same check the update route uses, persisted as
 * NULL when absent, nothing synthesised — and the field is sent normally below.
 *
 * IT IS THE MIRROR OF THE RULING THAT GOVERNS THE FIELD. `0031` makes
 * `display_name` optional so that a server requiring what the client does not
 * send cannot cause an outage; the opposite direction — a client sending what
 * the server does not accept — was open, and it was open *because the contract
 * said the field was there*.
 *
 * THE GATE THAT HELD THIS SHUT IS GONE, AND ITS ABSENCE IS DELIBERATE. A flag
 * pinned to `true` keeps a branch nobody reaches, and the unreachable half here
 * was a paragraph telling operators the name is recorded somewhere else — which
 * is now false. **Dead prose in a client is a stale assertion waiting for
 * someone to re-enable it**, so the branch went with the gate.
 */

/** What the operator must record. Held only while this screen is mounted. */
interface Outcome {
  readonly result: OnboardOrganizationOutput;
  readonly identifier: string;
  readonly password: string;
  readonly templateName: string;
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'deriving'; readonly progress: DerivationProgress }
  | { readonly kind: 'sending' };

export function OnboardOrganization({ onOnboarded }: { onOnboarded: () => void }) {
  const { locale, t } = useLocale();
  /*
   * THE PICKER IS A QUERY, AND ITS TWO STATES ARE DERIVED RATHER THAN STORED.
   *
   * `templates` was `readonly Template[] | null` where null meant "still
   * loading", and `templatesError` sat beside it — two pieces of state for one
   * request, which is how a screen ends up showing a spinner next to an error.
   * Both now come off the query.
   *
   * ONE CALL, ON MOUNT, unchanged: it is an audited platform call like every
   * other, so there is no polling and no refetch on focus. `lib/queries.ts`
   * carries the page-size reasoning and the note that creating a Template
   * invalidates this picker.
   */
  const picker = useTemplatePicker();
  const templates: readonly Template[] | null = picker.data?.data ?? null;
  const templatesError: ApiError | null = picker.error;
  const [identifier, setIdentifier] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [localError, setLocalError] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const busy = phase.kind !== 'idle';

  /*
   * THE WRITE, HELD FOR ITS INVALIDATION RATHER THAN ITS STATE. `phase` is
   * already a state machine over the derivation and the send, and the
   * derivation happens INSIDE it — so `onboard.isPending` would be a second,
   * narrower rendering of the same act. It is not read.
   *
   * What the hook buys is that a successful onboarding re-reads the
   * Organization list because it succeeded, not because this component
   * remembered to call back (`architecture.md` §3a).
   */
  const onboard = useOnboardOrganization();

  const submit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (busy) return;

      const refusal = identifierRefusal(identifier);
      if (refusal !== null) {
        setLocalError(refusal);
        return;
      }
      /*
       * THE NAME IS OPTIONAL, SO ONLY A NAME THE OPERATOR ACTUALLY TYPED IS
       * CHECKED. An empty field is omitted from the request and means "no name
       * recorded" — it is not an error, and treating it as one would make an
       * optional field required in the client while the contract says otherwise.
       */
      const nameRefusal =
        displayName === ''
          ? null
          : refusalText(displayNameRefusal(displayName), locale, t);
      if (nameRefusal !== null) {
        setNameError(nameRefusal);
        return;
      }
      if (templateId === '') {
        setLocalError(t('onboard.templateRequired'));
        return;
      }
      setLocalError(null);
      setNameError(null);
      setFailure(null);

      const chosen = templates?.find((template) => template.template_id === templateId);

      let derivationDone = false;
      setPhase({
        kind: 'deriving',
        progress: { fraction: 0, estimatedMs: null, elapsedMs: 0, usedWorker: true },
      });

      void createOnboardingCredential(identifier, (progress) => {
        if (progress.fraction >= 1) {
          derivationDone = true;
          setPhase({ kind: 'sending' });
          return;
        }
        if (!derivationDone) setPhase({ kind: 'deriving', progress });
      })
        .then(async (credential) => {
          const result = await onboard.mutateAsync({
            admin_identifier: credential.identifier,
            template_id: templateId,
            derived_value: credential.derivedValue,
            /*
             * OMITTED, NOT EMPTY, WHEN THERE IS NO NAME. "Absent" and "present
             * and empty" are different requests and `minLength: 1` accepts only
             * one of them, so a blank optional field would become a validation
             * error. Omitted means NULL, which means no name was recorded —
             * nothing here substitutes the identifier, the Template name or the
             * Workspace placeholder for a name the operator did not give.
             */
            ...(displayName === '' ? {} : { display_name: displayName }),
          });
          /*
           * THE CREDENTIAL IS PUT ON SCREEN ONLY HERE — after a 201. Everything
           * before this point can still fail, and a password shown beside a
           * failed create is worse than no password at all.
           */
          setOutcome({
            result,
            identifier: credential.identifier,
            password: credential.password,
            templateName: chosen?.name ?? templateId,
          });
          setPhase({ kind: 'idle' });
          setIdentifier('');
          setDisplayName('');
          setTemplateId('');
          onOnboarded();
        })
        .catch((thrown: unknown) => {
          setPhase({ kind: 'idle' });
          setFailure(toApiError(thrown));
        });
    },
    [busy, displayName, identifier, locale, onboard, onOnboarded, t, templateId, templates],
  );

  if (outcome !== null) {
    return (
      <CredentialPanel
        outcome={outcome}
        onDismiss={() => {
          setOutcome(null);
        }}
      />
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="grid gap-5 rounded-[12px] border border-line bg-surface p-5 sm:p-6"
    >
      <div>
        <h2 className="text-lg font-bold text-ink">{t('onboard.title')}</h2>
        <p className="mt-1 max-w-prose text-[0.875rem] leading-relaxed text-ink-muted">
          {t('onboard.intro')}
        </p>
      </div>

      {failure ? <OnboardingFailure failure={failure} /> : null}

      <Field
        id="admin-identifier"
        label={t('onboard.identifierLabel')}
        error={localError}
        hint={t('onboard.identifierHint')}
      >
        {(aria) => (
          <Input
            {...aria}
            type="email"
            value={identifier}
            onChange={(event) => {
              setIdentifier(event.target.value);
              if (localError) setLocalError(null);
            }}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            disabled={busy}
            required
          />
        )}
      </Field>

      {/*
        OPTIONAL, AND LABELLED AS OPTIONAL. `0031` makes it optional on the write
        path so the console can ship ahead of Core tightening it, and a form that
        required it would make an outage of the field's whole purpose. Blank is a
        legitimate answer meaning "no name recorded" — not an error, and not a
        placeholder this console invents.

        THE CR AND THE VAT REGISTRATION ARE NOT HERE, and that is the contract
        rather than a choice: `onboardOrganizationInput` is
        `additionalProperties: false` over five fields and has no room for
        either. The hint points at the page that does, which the success panel
        also links to.
      */}
      <Field
        id="organization-display-name"
        label={t('onboard.nameLabel')}
        error={nameError}
        hint={`${t('onboard.nameHintPrefix')} ${String(MAX_DISPLAY_NAME_LENGTH)} ${t('onboard.nameHintSuffix')}`}
      >
        {(aria) => (
          <Input
            {...aria}
            type="text"
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.target.value);
              if (nameError !== null) setNameError(null);
            }}
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
            maxLength={MAX_DISPLAY_NAME_LENGTH}
          />
        )}
      </Field>

      <Field
        id="template-id"
        label={t('onboard.templateLabel')}
        /*
          ⚠ THIS SAID "It cannot be changed afterwards." IT CAN —
          `platform.organizations.set-template`, which also CLEARS. Read at the
          moment of the decision, so it made a reversible choice look like a
          commitment and would have made an operator hesitate over something
          they could undo.
        */
        hint={t('onboard.templateHint')}
      >
        {(aria) => (
          <select
            {...aria}
            value={templateId}
            onChange={(event) => {
              setTemplateId(event.target.value);
              if (localError) setLocalError(null);
            }}
            disabled={busy || templates === null || templates.length === 0}
            required
            className={cn(
              'w-full min-h-10 px-3 py-2 rounded-[7px] bg-surface text-ink',
              'border border-line-strong transition-colors hover:border-ink-faint',
              'aria-invalid:border-scarlet-600',
            )}
          >
            <option value="">
              {templates === null
                ? t('onboard.templatesLoading')
                : templates.length === 0
                  ? t('onboard.templatesEmpty')
                  : t('onboard.templatesChoose')}
            </option>
            {(templates ?? []).map((template) => (
              <option key={template.template_id} value={template.template_id}>
                {template.name}
              </option>
            ))}
          </select>
        )}
      </Field>

      {templatesError ? (
        <p role="alert" className="text-[0.875rem] text-scarlet-700">
          {t('onboard.templatesFailed')}
        </p>
      ) : null}

      {templates !== null && templates.length === 0 ? (
        <p className="rounded-[7px] border border-gold-500 bg-gold-50 p-3 text-[0.875rem] leading-relaxed text-ink">
          {t('onboard.templatesNeeded')}
        </p>
      ) : null}

      {/*
        THE WORKSPACE NAME IS NOT ASKED FOR, AND THE ABSENCE IS EXPLAINED WHERE
        AN OPERATOR WOULD LOOK FOR THE FIELD — not in a footnote, and not left to
        be noticed.

        ⚠ THE WORDING CHANGED WHEN IT MOVED INTO THE DICTIONARY. It said Dudo
        has nowhere to store a workspace name YET and that naming ARRIVES with
        the organization-structure work — an absence claim plus a schedule, both
        of which expire silently. It now says which surface OWNS the field,
        which stays true after that surface ships. See `onboard.workspaceBody`.
      */}
      <p className="rounded-[7px] border border-line bg-sunk/60 p-3 text-[0.8125rem] leading-relaxed text-ink-soft">
        <span className="font-semibold">{t('onboard.workspaceLead')}</span>{' '}
        {t('onboard.workspaceBody')}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          disabled={busy || templates === null || templates.length === 0}
          busy={busy}
        >
          {phase.kind === 'deriving'
            ? t('onboard.submitDeriving')
            : phase.kind === 'sending'
              ? t('onboard.submitSending')
              : t('onboard.submit')}
        </Button>
        <p className="text-[0.8125rem] text-ink-muted">{t('onboard.shownOnce')}</p>
      </div>

      {phase.kind === 'deriving' ? <DerivationBar progress={phase.progress} /> : null}
      {phase.kind === 'sending' ? <LoadingBlock label={t('onboard.creating')} /> : null}
    </form>
  );
}

function OnboardingFailure({ failure }: { failure: ApiError }) {
  const t = useT();
  return (
    <div
      role="alert"
      className="rounded-[7px] border border-scarlet-600 bg-scarlet-50 p-4 text-[0.875rem]"
    >
      <p className="font-bold text-scarlet-700">
        {failure.code === 'conflict'
          ? t('onboard.failed.conflict')
          : failure.code === 'not_found'
            ? t('onboard.failed.notFound')
            : failure.code === 'quota_exceeded'
              ? t('onboard.failed.quota')
              : t('onboard.failed.other')}
      </p>
      <p className="mt-1 leading-relaxed text-ink-soft">
        {failure.code === 'conflict' ? (
          <>
            {t('onboard.failed.conflictBody')}{' '}
            <span className="font-semibold">{t('onboard.failed.nothingCreated')}</span>{' '}
            {t('onboard.failed.conflictTail')}
          </>
        ) : failure.code === 'not_found' ? (
          <>
            {t('onboard.failed.notFoundBody')}{' '}
            <span className="font-semibold">{t('onboard.failed.nothingCreated')}</span>{' '}
            {t('onboard.failed.notFoundTail')}
          </>
        ) : failure.code === 'quota_exceeded' ? (
          <>
            {t('onboard.failed.quotaBody')}{' '}
            <span className="font-semibold">{t('onboard.failed.nothingCreatedBare')}</span>{' '}
            {t('onboard.failed.quotaTail')}
          </>
        ) : (
          <>
            {failure.message}{' '}
            <span className="font-semibold">{t('onboard.failed.otherWarning')}</span>
          </>
        )}
      </p>
      {failure.details.length > 0 ? (
        <ul className="mt-2 grid list-disc gap-1 ps-4 text-ink-soft">
          {failure.details.map((detail) => (
            <li key={`${detail.field}:${detail.issue}`}>
              {/*
                THE FIELD NAME IS CORE'S AND IS NOT TRANSLATED — it is a wire
                identifier the operator quotes when reporting. `bdi` isolates it
                because a Latin token inside an RTL list item otherwise drags
                the surrounding punctuation with it.
              */}
              <bdi className="font-mono text-[0.8125rem]">{detail.field}</bdi> — {detail.issue}
            </li>
          ))}
        </ul>
      ) : null}
      {failure.request_id ? (
        <p className="mt-2 text-xs text-ink-muted">
          {t('denied.reference')}{' '}
          <bdi className="font-mono break-all">{failure.request_id}</bdi>
        </p>
      ) : null}
    </div>
  );
}

/**
 * The credential, shown once.
 *
 * DISMISSAL IS TWO STEPS ON PURPOSE. A single "Done" button beside the only copy
 * of a credential is a mis-click away from destroying it, and there is no undo
 * and no way to recover the value afterwards.
 */
function CredentialPanel({ outcome, onDismiss }: { outcome: Outcome; onDismiss: () => void }) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const warnings = outcome.result.warnings;

  return (
    <section
      aria-labelledby="credential-heading"
      className="rounded-[12px] border-2 border-green-500 bg-surface p-5 sm:p-6"
    >
      {/*
        ⚠ THE HEADING WAS `{outcome.templateName} business created`, AND THE
        TEMPLATE NAME MOVED OUT OF IT RATHER THAN BEING INTERPOLATED.

        An operator-typed Template name embedded mid-heading needs a different
        word order in Arabic, and a prefix/suffix pair around a proper noun in a
        HEADING reads badly in both languages. **The type is a recorded fact
        about what was just created, so it belongs in the list of recorded
        facts** — beside the Organization, the administrator and the workspace —
        where it is also easier to find. The heading is now stable in both
        languages and carries no interpolation at all.
      */}
      <h2 id="credential-heading" className="text-lg font-bold text-green-700">
        {t('onboard.createdTitle')}
      </h2>

      {warnings.length > 0 ? <WarningPanel warnings={warnings} /> : null}

      <p className="mt-4 leading-relaxed text-ink">
        <span className="font-bold">{t('onboard.record')}</span> {t('onboard.recordWhy')}
      </p>

      <dl className="mt-4 grid gap-3">
        <div>
          <dt className="text-xs font-semibold tracking-[0.04em] uppercase text-ink-faint">
            {t('onboard.signInWith')}
          </dt>
          <dd className="mt-1 text-[0.9375rem] text-ink">
            <bdi className="font-mono break-all">{outcome.identifier}</bdi>
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-[0.04em] uppercase text-ink-faint">
            {t('onboard.password')}
          </dt>
          {/*
            `select-all` so one click selects the whole value — a 32-character
            base64url string is easy to truncate by hand, and a truncated
            password is indistinguishable from a wrong one at the login screen.
            No "copy" button: the clipboard is a second copy this console cannot
            supervise, and writing one is a decision for whoever needs it.
          */}
          <dd className="mt-1 rounded-[7px] border border-line-strong bg-sunk px-3 py-2 text-[0.9375rem] text-ink">
            <bdi className="font-mono break-all select-all">{outcome.password}</bdi>
          </dd>
        </div>
      </dl>

      <dl className="mt-5 grid gap-x-6 gap-y-2 border-t border-line pt-4 text-[0.8125rem] sm:grid-cols-2">
        {/*
          THE TEMPLATE NAME IS OPERATOR-TYPED AND IS NOT TRANSLATED — only its
          LABEL is. `bdi` because the name may be Arabic on an English page or
          the reverse, and this list sits beside three Latin identifiers.
        */}
        <div className="min-w-0">
          <dt className="font-semibold text-ink-soft">{t('onboard.businessType')}</dt>
          <dd className="break-words text-ink-muted">
            <bdi>{outcome.templateName}</bdi>
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="font-semibold text-ink-soft">{t('onboard.organizationId')}</dt>
          <dd className="text-ink-muted">
            <bdi className="font-mono break-all">{outcome.result.organization_id}</bdi>
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="font-semibold text-ink-soft">{t('onboard.administratorId')}</dt>
          <dd className="text-ink-muted">
            <bdi className="font-mono break-all">{outcome.result.admin_principal_id}</bdi>
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="font-semibold text-ink-soft">{t('onboard.workspaceId')}</dt>
          <dd className="text-ink-muted">
            {outcome.result.workspace_id === null ? (
              t('onboard.workspaceNotCreated')
            ) : (
              <bdi className="font-mono break-all">{outcome.result.workspace_id}</bdi>
            )}
          </dd>
        </div>
      </dl>

      {/*
        `0026`'s accepted cost, said plainly rather than left for an operator to
        discover. There is no self-service password change, so this is not a
        temporary password in the usual sense.
      */}
      <p className="mt-5 rounded-[7px] border border-gold-500 bg-gold-50 p-3 text-[0.8125rem] leading-relaxed text-ink">
        <span className="font-semibold">{t('onboard.youWillKnow')}</span>{' '}
        {t('onboard.noSelfService')}
      </p>

      {/*
        WHERE THE REST OF THE RECORD GOES. Onboarding cannot accept a CR or a
        VAT registration — the contract has no field for either and every
        Organization starts `not_recorded` — so the next step is named here
        rather than left to be discovered. THIS IS A LINK AND NOT A REDIRECT:
        the password is on this screen and navigating away from it destroys the
        only copy in existence.
      */}
      <p className="mt-5 rounded-[7px] border border-line bg-sunk/60 p-3 text-[0.8125rem] leading-relaxed text-ink-soft">
        <span className="font-semibold">{t('onboard.recordBeforeFollowing')}</span>{' '}
        {t('onboard.identityMissing')}{' '}
        <Link
          to="/organizations/$organizationId"
          params={{ organizationId: outcome.result.organization_id }}
          className="font-semibold text-navy-600 no-underline hover:underline"
        >
          {t('onboard.openBusiness')}
        </Link>
        {/*
          THE FULL STOP MOVED INSIDE THE FOLLOWING SENTENCE. A bare `.` sitting
          between a link and the next sentence is a lone punctuation node, and
          in RTL the bidirectional algorithm resolves it against its
          neighbours — so it can render on the wrong side of the link. Arabic
          also does not want an English full stop here at all.
        */}{' '}
        {t('onboard.leavingLoses')}
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {confirming ? (
          <>
            <p className="w-full font-semibold text-ink">{t('onboard.dismissAsk')}</p>
            <Button variant="primary" onClick={onDismiss}>
              {t('onboard.dismissYes')}
            </Button>
            {/*
              ⚠ THIS BUTTON SAID "Not yet". It was the one place in the copy
              pass where the absence check's `\byet\b` would have been a genuine
              false positive — an operator's answer about their OWN state, not a
              claim the system makes about itself. **The words changed rather
              than the check**, because a skip is the highest-suspicion change
              there is and this replacement says what the button DOES.
            */}
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('onboard.dismissNo')}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t('onboard.dismissStart')}
          </Button>
        )}
      </div>
    </section>
  );
}

/**
 * A non-empty `warnings` array. Prominent, and NOT an error.
 *
 * The schema requires a console to render this prominently and says that
 * treating it as an ordinary success is the defect the field exists to prevent.
 * It is equally wrong to render it as a failure: the Organization, the admin and
 * the credential all exist.
 */
function WarningPanel({ warnings }: { warnings: readonly string[] }) {
  const t = useT();
  return (
    <div
      role="status"
      className="mt-4 rounded-[7px] border border-gold-500 bg-gold-50 p-4 text-[0.875rem] leading-relaxed text-ink"
    >
      <p className="font-bold">{t('onboard.warning.title')}</p>
      <ul className="mt-2 grid list-disc gap-2 ps-4">
        {warnings.map((warning) => (
          <li key={warning}>
            {!isKnownOnboardingWarning(warning) ? (
              /*
               * Checked FIRST, so the two known branches below cannot be reached
               * by a token that merely resembles one. `onboardingWarning` is a
               * closed set of two; anything else means Core reports something
               * newer than this build.
               *
               * SHOWN VERBATIM RATHER THAN HIDDEN. An unrecognised warning is
               * still a warning, and dropping it would be worse than failing to
               * explain it — the operator would read a partially-completed
               * onboarding as a clean one.
               */
              <>
                <span className="font-semibold">{t('onboard.warning.unknownLead')}</span>{' '}
                {/*
                  THE TOKEN IS CORE'S AND IS SHOWN VERBATIM. `bdi` for the same
                  reason as everywhere else on this screen; the trailing full
                  stop was removed because a lone `.` after an isolated Latin
                  token is exactly the character RTL reorders.
                */}
                <bdi className="font-mono">{warning}</bdi>{' '}
                {t('onboard.warning.unknownBody')}
              </>
            ) : warning === 'first_workspace_not_created' ? (
              <>
                <span className="font-semibold">{t('onboard.warning.workspaceLead')}</span>{' '}
                {t('onboard.warning.workspaceBody')}
              </>
            ) : (
              <>
                <span className="font-semibold">{t('onboard.warning.auditLead')}</span>{' '}
                {t('onboard.warning.auditBody')}
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DerivationBar({ progress }: { progress: DerivationProgress }) {
  const { locale, t } = useLocale();
  const percent = Math.round(progress.fraction * 100);
  const remainingMs =
    progress.estimatedMs === null ? null : Math.max(0, progress.estimatedMs - progress.elapsedMs);
  return (
    <div className="grid gap-2">
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('onboard.progressLabel')}
        className="h-1.5 w-full overflow-hidden rounded-full bg-sunk"
      >
        <div
          className="h-full rounded-full bg-navy-600 transition-[width] duration-150 ease-linear"
          style={{ width: `${String(percent)}%` }}
        />
      </div>
      <p aria-live="polite" className="text-[0.8125rem] text-ink-muted">
        {remainingMs === null
          ? t('derivation.measuring')
          : /*
              THREE PIECES, AND THE MIDDLE ONE IS `Intl`'s — the same shape
              `SignIn` uses, from the same three keys. `${n} seconds` is wrong
              in Arabic for almost every `n`, and `formatSeconds` defers that to
              the engine. **The keys moved out of the `signIn.` namespace when
              this screen became the second consumer**, rather than being
              copied: two dictionaries of the same sentence drift, and nothing
              goes red when they do.
            */
            `${t('derivation.remainingPrefix')} ${formatSeconds(locale, Math.ceil(remainingMs / 1000))} — ${t('derivation.estimateNote')}`}
      </p>
    </div>
  );
}
